import React, {Component} from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import uniq from 'lodash/uniq';
import {FormattedMessage, injectIntl} from 'react-intl';

import {
  getEntitySEOObj,
  getEntitySeoTitle,
  getEntitySearchAPIParams,
  getHeroImage,
  isPerformerEntity,
  isGroupingEntity,
  isCategoryEntity,
  isVenueEntity,
  isGeography,
  ENTITY_TYPE_VENUE,
  ENTITY_TYPE_GEO,
  ENTITY_TYPE_PERFORMER,
} from '@stubhub/entity-helper';
import {publish} from '@stubhub/pubsub-utils';
import {CloudinaryImage, helper} from '@stubhub/react-cloudinary';
import FeatureToggle from '@stubhub/react-feature-toggle';
import GuaranteeCard from '@stubhub/react-guarantee';
import InterceptModal from '@stubhub/react-intercept-modal';
import SpotifyWidget from '@stubhub/react-spotify-widget';
import {connect, ssr_wait} from '@stubhub/react-store-provider';
import {theme} from '@stubhub/react-theme-provider';
import withQueryParams from '@stubhub/react-with-query-params';

// eslint-disable-next-line import/no-named-as-default
import {isNotFoundEntity} from '../../helpers/entity-helpers';
import NotFoundModule from '../../modules/react-404-module';
import {CardImage} from '../../modules/react-card';
import CBTModule from '../../modules/react-cbt-module';
import ConnectedCityPage from '../../modules/react-city-page';
import Email from '../../modules/react-email-collection/src';
import SEOSideContent from '../../modules/react-entity-side-rail-details';
import EventListPanelContainer from '../../modules/react-event-list-panel/event-list-panel-lazyload';
import {sortValues} from '../../modules/react-event-sorter/messages';
import JumbotronBanner from '../../modules/react-jumbotron-banner/jumbotronBanner';
import EntityPillBar from '../../modules/react-lazyload-pill-bar/pill-bar-lazyload';
import ConnectedRelatedArtists from '../../modules/react-related-artists';
import NoResultsPanel from '../../modules/react-search-no-results-panel';
import SeoBottomContent from '../../modules/react-seo-content';
import SeoNearbyVenues from '../../modules/react-seo-nearby-venues';
import ConnectedSeoPerformerVenues, {LIST_TYPE as SEOVENUESTYPE} from '../../modules/react-seo-performer-venues';
import ConnectedSEOVenuePerformers from '../../modules/react-seo-venue-performers';
import {ENTITY_EVENTS} from '../../tracking/track-enum';
import {DEFAULT_IMAGE_CONFIG} from '../../utils/cloudinaryConfigs';
import {
  DISPLAY_TYPE_DATE_CARDS,
  DISPLAY_TYPE_CALENDAR,
  DISPLAY_TYPE_EVENTS_ONLY,
  DISPLAY_TYPE_VIP,
  DISPLAY_TYPE_OTHER,
} from '../../utils/constants';
// eslint-disable-next-line import/no-named-as-default
import isBannedEntity, {hideSEOEntity} from '../../utils/uk-cbt-banned-sites';
import ConnectedEventSEOList from '../entity/react-entity-event-seo-list';

import getEntityConfigObj from './config';
import controller, {NAMESPACE} from './controller';
import ContentEntityLayout from './layouts/content-layout';
import DefaultEntityLayout from './layouts/default-layout';
import {messages} from './messages';
import EntitiesSeoLinks from './react-entity-seo-links';
import './styleLoader';

const LayoutTypes = {
  Default: DefaultEntityLayout,
  Content: ContentEntityLayout,
};

const StrongChunk = (chunks) => <strong>{chunks}</strong>;

const DEFAULT_INNER_WIDTH = 600;
const SMALL_WIDTH_LIMIT = 640;

class EntityPagePlaceholder extends Component {
  render() {
    const _ = 'EntityPagePlaceholder';
    const container = cs(_);
    const banner = cs(`${_}__banner`);
    const eventsHeader = cs(`${_}__eventsHeader`);
    const eventsCell = cs(`${_}__eventsCell`);

    return (
      <div className={container}>
        <div className={banner} />
        <div className={eventsHeader} />
        <div className={eventsCell} />
        <div className={eventsCell} />
        <div className={eventsCell} />
        <div className={eventsCell} />
        <div className={eventsCell} />
        <div className={eventsCell} />
        <div className={eventsCell} />
      </div>
    );
  }
}

/**
 * Represents the EntityPage component.
 * @class
 * @extends Component
 */
@theme
@withQueryParams({pbE: 'pbE', pbN: 'pbN', pbS: 'pbS', pbH: 'pbH', pbC: 'pbC'})
@connect(controller)
class EntityPage extends Component {
  static contextTypes = {
    cookies: PropTypes.object,
    queue: PropTypes.object,
    globalRegistry: PropTypes.object,
  };

  static defaultProps = {
    referrer: '',
    ssr: true,
  };

  @ssr_wait
  init() {
    const {entityId, entityType} = this.props;
    this.loadEntity(entityId, entityType);
  }

  cityPagethemeBackgroundColor = (theme = {}, cxs) =>
    theme.unifiedLayout &&
    theme.unifiedLayout.paletteSecondary &&
    cxs({
      backgroundColor: `${theme.unifiedLayout.paletteSecondary} !important`,
    });

  componentDidMount() {
    const {entity, entityId} = this.props;

    /*
     * The only moment the below is true is on SSR rendered first load.
     * All the other cases should fall under componentDidUpdate case's umbrella
     */
    if (entity && entityId && entity.id && entity.id.toString() === entityId) {
      publish(ENTITY_EVENTS.LOADED);
    }
  }

  /**
   * React Component Lifecycle.
   *
   */
  UNSAFE_componentWillMount() {
    const {entity, entityId, entityType, pid} = this.props;

    if (!entity || Object.keys(entity).length === 0 || pid !== `${entityType}/${entityId}`) {
      this.init();
    }
  }

  componentDidUpdate(prevProps) {
    const {entity: previousEntity} = prevProps;
    const {entity: currentEntity} = this.props;

    if (
      (!previousEntity && currentEntity) ||
      (previousEntity && currentEntity && previousEntity.id !== currentEntity.id)
    ) {
      publish(ENTITY_EVENTS.LOADED);
    }
  }

  componentWillUnmount() {
    const {resetEntityPage} = this.props;
    resetEntityPage();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    /**
     * Previous Entity Info
     *
     */

    const {entityId, entityType} = this.props;
    const previousEntityId = entityId;
    const previousEntityType = entityType;

    /**
     * Next Entity Info
     *
     */
    const newEntityId = nextProps.entityId;
    const nextEntityType = nextProps.entityType;

    /**
     * Reload entity and trigger reload events(by change props.entityId) when entityId changed
     */
    if (
      previousEntityId &&
      newEntityId &&
      nextEntityType &&
      (previousEntityId !== newEntityId || previousEntityType !== nextEntityType)
    ) {
      this.loadEntity(newEntityId, nextEntityType);
    }
  }

  loadEntity = (entityId, entityType) => {
    const {loadEntity, updateTheme} = this.props;

    return loadEntity(entityType, entityId, updateTheme);
  };

  render() {
    const {entity, entityId, entityType, theme, themeProvider, error} = this.props;
    let innerCmp = null;
    const cityPagethemeBackgroundColorClassName = this.cityPagethemeBackgroundColor(theme, themeProvider);
    if (entity && entityId && entityType) {
      const _props = {...this.props};
      innerCmp = <EntityPageInner {..._props} loading={<EntityPagePlaceholder />} />;
    }

    const showNotFound = isNotFoundEntity(error);

    return (
      <div>
        <div id="app-container" className={cs('EntityPage', cityPagethemeBackgroundColorClassName)}>
          {showNotFound ? <NotFoundModule /> : entity ? innerCmp : <EntityPagePlaceholder />}
        </div>
      </div>
    );
  }
}

EntityPage.propTypes = {
  /** The intl object */
  intl: PropTypes.object.isRequired,

  /** Page Referrer */
  referrer: PropTypes.string,

  /** Controller Method to Load the Entity Info from the API. */
  loadEntity: PropTypes.func.isRequired,

  /** Prop indicating the device type of the client */
  deviceType: PropTypes.string,

  /** Object containing the Catalog Entity API response. */
  entity: PropTypes.object,

  /** The id associated to the Entity. */
  entityId: PropTypes.string,

  /** Object containing the URL related Params. */
  entityType: PropTypes.string,

  /** The field having the count of the Geography SEO Links used by the Geo Pages to choose between Displaying Geo or Venues */
  geographiesSEOLinkCount: PropTypes.number,

  /** Object Containing React Route Params */
  localEventIndex: PropTypes.number,

  /** Object Containing the Parsed URL Information  */
  location: PropTypes.object,

  /** Flag indicating if No Events were loaded in the current view.  */
  noEvent: PropTypes.bool,

  /** Page ID */
  pid: PropTypes.string,

  /** Update query function */
  replaceQuery: PropTypes.func.isRequired,

  /** Function to reset the redux store for the Entity page. */
  resetEntityPage: PropTypes.func.isRequired,

  /** If ssr */
  ssr: PropTypes.bool,

  /** Current Global theme object */
  theme: PropTypes.object,

  /** Cxs function */
  themeProvider: PropTypes.func,

  /** Cxs object for applying the theme. */
  updateTheme: PropTypes.func.isRequired,

  /** Error object, if there has been any error loading the entity info */
  error: PropTypes.object,
};

class EntityPageInner extends Component {
  static contextTypes = {
    queue: PropTypes.object,
    globalRegistry: PropTypes.object,
    cookies: PropTypes.object,
  };

  constructor(props, context = {}) {
    super(props, context);
    const {intl, theme = {}, query: options = {}, replaceQuery, deviceType} = props;
    const {globalRegistry = {}} = context || {};

    /**
     * Translate Commonly Used Text in code and Cache them.
     *
     */
    this.nestedSubHeading = intl.formatMessage(messages.subHeading);

    /**
     * Setting the Default State of the Partner Page and City Header boolean.
     *
     */
    this.state = {displayType: DISPLAY_TYPE_EVENTS_ONLY};

    if (typeof globalRegistry.getFeatureValue === 'function') {
      /**
       * Geography new city page Toggle
       *
       */
      this.isCityPageEnabled = globalRegistry.getFeatureValue('geography.cityPage.enable');
      this.isGeoLevelCheckDisabled = globalRegistry.getFeatureValue('geography.cityPage.geoLevelCheck.disable');
      this.isDateFilterEnabledOnCityPage = globalRegistry.getFeatureValue('geography.citypage.enableDateFilter');

      /**
       * Toggle to control whether to enable related artists on performer page
       *
       */
      this.isRelatedArtistsEnabled = globalRegistry.getFeatureValue('browse.relatedArtists.enable');

      /**
       * Toggle to control whether to enable  venues seo on performer page
       *
       */
      this.isSEOPerformerVenuesEnable = globalRegistry.getFeatureValue('browse.venue.performerVenues.enable');

      /**
       * Toggle to control whether to enable  venues seo on performer page
       *
       */
      this.isSEOGroupingVenuesEnable = globalRegistry.getFeatureValue('browse.venue.groupingVenues.enable');

      /**
       * Toggle to control whether to enable nearby venues on venue page
       *
       */
      this.isNearbyVenuesEnabled = globalRegistry.getFeatureValue('browse.venue.nearbyVenues.enable');

      /**
       * Toggle to control whether to enable venue performers on venue page
       *
       */
      this.isVenuePerformersEnabled = globalRegistry.getFeatureValue('browse.venue.venuePerformers.enable');

      /**
       * ReadCrumbs hideHome isUseCleanName
       */
      this.isHideHome = globalRegistry.getFeatureValue('breadCrumb.hideHome');
      this.isUseCleanName = globalRegistry.getFeatureValue('breadCrumb.useCleanName');

      /**
       * Toggle for query params
       */
      this.useQueryParams = globalRegistry.getFeatureValue('common.queryParams');

      // Feature toggle to hide left-side pill bar, so that there is no upcoming & categories tabs
      this.hideTabsConfigOnCategoryGrouping = globalRegistry.getFeatureValue(
        'common.hideTabsConfigOnCategoryGroupingEntity'
      );
      const {
        entity: {parentGroupingId, entityType, id},
      } = this.props;
      this.isEPLGrouping =
        globalRegistry.getFeatureValue('homepage.showUKcmaFeatures.enableEPLChanges') &&
        isBannedEntity({id, entityType, parentGroupingId});
    }

    if (this.useQueryParams) {
      if (options.pbS) {
        const key =
          options.pbS === 'd'
            ? 'date'
            : options.pbS === 'i'
            ? 'distance'
            : options.pbS === 'p'
            ? 'price'
            : options.pbS === 'o'
            ? 'popularity'
            : 'date';
        this.state.sortProps = sortValues[key];
      }
      if (theme.isPartner && (options.pbC || options.pbE)) {
        this.state = {
          displayType:
            options.pbE === 'o'
              ? DISPLAY_TYPE_OTHER
              : options.pbC === 'c'
              ? deviceType === 'phone' || this.isSmall()
                ? DISPLAY_TYPE_DATE_CARDS
                : DISPLAY_TYPE_CALENDAR
              : DISPLAY_TYPE_EVENTS_ONLY,
        };
        if (options.pbE === 'p') {
          options.pbC = undefined;
          replaceQuery(options);
        }
      }
      if (options.pbE) {
        this.state = {
          displayType:
            options.pbE === 'o'
              ? DISPLAY_TYPE_OTHER
              : options.pbE === 'v'
              ? DISPLAY_TYPE_VIP
              : DISPLAY_TYPE_EVENTS_ONLY,
        };
      }
    }
  }

  isSmall = () => {
    const windowWidth = __CLIENT__ ? window.innerWidth : DEFAULT_INNER_WIDTH;

    return windowWidth < SMALL_WIDTH_LIMIT;
  };

  /**
   * Method to Change the Display Type using the tabs
   * @param displayType [String] The Display Type that needs to be set.
   *
   */
  changeDisplayType = (displayType) => {
    const {deviceType, replaceQuery} = this.props;
    displayType =
      displayType === DISPLAY_TYPE_CALENDAR && (deviceType === 'phone' || this.isSmall())
        ? DISPLAY_TYPE_DATE_CARDS
        : displayType;

    if (this.useQueryParams) {
      const pbOptions =
        displayType === DISPLAY_TYPE_CALENDAR || displayType === DISPLAY_TYPE_DATE_CARDS
          ? {pbC: 'c'}
          : displayType === 'CATEGORIES'
          ? {pbN: 'c'}
          : displayType === 'UPCOMING_EVENTS'
          ? {pbN: 'u'}
          : displayType === DISPLAY_TYPE_VIP
          ? {pbE: 'v'}
          : displayType === DISPLAY_TYPE_OTHER
          ? {pbE: 'o'}
          : displayType === DISPLAY_TYPE_EVENTS_ONLY
          ? {pbE: 'e'}
          : {};
      if (pbOptions.pbE) {
        pbOptions.pbC = undefined;
      } else if (pbOptions.pbC) {
        pbOptions.pbE = undefined;
      }
      replaceQuery(pbOptions);
    }

    displayType = displayType === 'UPCOMING_EVENTS' ? DISPLAY_TYPE_EVENTS_ONLY : displayType;
    this.setState((state) => {
      const {sortProps = {}} = state;
      const {sort = 'eventDateLocal asc'} = sortProps;
      sortProps.sort = uniq(`${sort},distance asc,popularity desc`.split(',')).join(',');

      return {
        displayType,
        sortProps,
      };
    });
  };

  /**
   * Method to format the Title for the Event List Sections.
   * @param numFound [number] The number of the events found for that Event list.
   * @param includeRadius [boolean] The Flag indicating if the Events selected were nearby or other locations.
   * @param geo [object] The geo object containing the location information for the user.
   *
   */
  formatTitle = ({numFound = 0, includeRadius, geo}) => {
    if (geo && geo.key && includeRadius) {
      return (
        <FormattedMessage
          id="Performer.events.near.location"
          defaultMessage="{numFound, plural, =0 {No events} one {<strong>#</strong> event} other {<strong>#</strong> events}} near <strong>{name}</strong>"
          values={{
            ...geo,
            numFound,
            strong: StrongChunk,
          }}
        />
      );
    }

    return (
      <FormattedMessage
        id="Performer.events.all.location"
        defaultMessage="{numFound, plural, =0 {No events} one {<strong>#</strong> event} other {<strong>#</strong> events}} in all locations"
        values={{
          numFound,
          strong: StrongChunk,
        }}
      />
    );
  };

  /**
   * Method to get the Nested Subheading.
   * @param entity [object] The instance of the Catalog Entity Object from API.
   * @returns nestedSubHeading [string] The String to be used as the nested sub heading.
   *
   */
  getNestedSubHeading(entity = {}) {
    const {entityType} = entity;

    if (isGeography(entityType)) {
      return '';
    } else if (isVenueEntity(entityType)) {
      const {address = {}} = entity;
      const {postalCode, address1, locality, state} = address;

      return `${address1}, ${postalCode ? `${locality}, ${postalCode}` : `${locality}, ${state}`}`;
    }

    return this.nestedSubHeading;
  }

  /**
   * Get grouping flags used by sport breadcrumbs customized path.
   * @param {object} entity info
   */
  getSportCustomizedFlag = ({isMLB, isNFL, isNBA, isMLS, isNHL, isNCAAFootball, isNCAABasketball, isNCAABaseball}) =>
    isMLB || isNFL || isNBA || isMLS || isNHL || isNCAAFootball || isNCAABasketball || isNCAABaseball;

  /**
   * Check required data for sports customized breadcrumbs.
   * @param {object} ancestors
   */
  getCustomizedBreadcrumbData = (ancestors) => {
    const categoryCheck = ancestors.categories && ancestors.categories[0];
    const groupingCheck = ancestors.groupings && ancestors.groupings[0];
    if (this.isHideHome) {
      return categoryCheck && groupingCheck && [...ancestors.categories, ancestors.groupings[0]];
    }

    return categoryCheck && groupingCheck && [ancestors.categories[0], ancestors.groupings[0]];
  };

  /**
   * Method to parse the Catalog Entity API Response and build a Header Data object for Hero Header Component
   * TODO - Partner Logo Sections `partners`
   *
   */
  getJumbotronBannerData = ({entity, theme = {}, intl}, isCityHeader) => {
    let headerData = {};
    const breadcrumbConfig = {
      breadcrumbData: null,
      showHome: !this.isHideHome,
      useCleanName: !!this.isUseCleanName,
    };
    let jumbotronType = 'default';

    if (entity) {
      const {entityType, ancestors} = entity;
      const isVenue = entityType === ENTITY_TYPE_VENUE;
      if (ancestors) {
        /*
         * Customized breadcrumbData when entityType eq performer as /category0/grouping0/
         * this.getSportCustomizedFlag(entity) &&
         */
        if (this.isHideHome) {
          breadcrumbConfig.breadcrumbData =
            entityType === ENTITY_TYPE_PERFORMER && this.getCustomizedBreadcrumbData(ancestors);
        } else {
          breadcrumbConfig.breadcrumbData =
            this.getSportCustomizedFlag(entity) &&
            entityType === ENTITY_TYPE_PERFORMER &&
            this.getCustomizedBreadcrumbData(ancestors);
        }

        if (!breadcrumbConfig.breadcrumbData) {
          // BreadcrumbConfig.showHome = true;
          breadcrumbConfig.breadcrumbData =
            isVenue || entityType === ENTITY_TYPE_GEO ? ancestors.geographies : ancestors.categories;
        }
      }

      if (isCityHeader) {
        jumbotronType = 'landing';
      }

      if (theme && theme.bannerImagery) {
        jumbotronType = 'featuredImg';
      }

      const subHeading = this.getNestedSubHeading(entity);

      headerData = {
        id: entity.id,
        heading: isVenue
          ? entity.cleanName
          : intl.formatMessage(messages.heading, {heading: entity.cleanName, nestedSubHeading: subHeading}),
        nestedSubHeading: isVenue ? subHeading : null,
        image: getHeroImage(entityType, entity, {noCompress: isCityHeader}),
        backgroundLogo: theme.headerLogo,
        backgroundColor: theme.headerBackgroundColor,
        jumbotronType,
        breadcrumbConfig,
      };
    }

    return headerData;
  };

  /**
   * Get city dynamic attributes from catalog api.
   * @param {Array} dynamicAttributes=[] dynamic attributes
   * @param {Array} images=[] images attributes
   * @returns {object} city page related attributes
   */
  getCityPageAttributes = (dynamicAttributes = [], images = []) => {
    let cityDescAttribute = '';
    let cityImageAttribute = '';
    let isCityPageValidAttribute = false;
    const cityHeaderAttr = dynamicAttributes.find((item) => item.name === 'header_copy');
    if (cityHeaderAttr) {
      isCityPageValidAttribute = true;
      cityDescAttribute = cityHeaderAttr.value;
    }

    // Get primary image or first image
    if (images && images.length > 0) {
      const [image] = images.filter((item) => item && item.primaryInd);
      cityImageAttribute = image ? image.url : (images[0] || {}).url;
    }

    return {
      isCityPageValidAttribute,
      cityImageAttribute,
      cityDescAttribute,
    };
  };

  setSort = (sortItem) => {
    const {replaceQuery, dispatch} = this.props;
    const {key, value: sortProps = {}} = sortItem;

    if (this.useQueryParams) {
      const pbOptions =
        key === 'date'
          ? {pbS: 'd'}
          : key === 'distance'
          ? {pbS: 'i'}
          : key === 'price'
          ? {pbS: 'p'}
          : key === 'popularity'
          ? {pbS: 'o'}
          : {};
      replaceQuery(pbOptions);
    }
    if (typeof dispatch === 'function') {
      dispatch({type: 'SORT_SELECTOR_CHANGED'});
    }
    this.setState({sortProps});
  };

  setSearch = (searchItem) => {
    const {replaceQuery} = this.props;
    const {value = {}} = searchItem;
    const {sort: sortProps = ''} = value;

    if (this.useQueryParams) {
      const pbOptions = {};
      replaceQuery(pbOptions);
    }
    this.setState({sortProps});
  };

  componentDidMount() {
    const {entity} = this.props;

    /**
     * Set/update the document title based on the Entity
     *
     */
    if (typeof document !== 'undefined') {
      document.title = getEntitySeoTitle(entity);
    }
  }

  render() {
    const {
      geo,
      entity,
      theme = {},
      localEventIndex,
      noEvent,
      geographiesSEOLinkCount,
      entityId,
      entityType,
      deviceType,
      currentLocation = {},
      isCBTModuleEnabled,
      isCBTModuleException,
      showTrustMessages,
      hasSeoBottomContent,
      spotifyArtistId,
    } = this.props;
    let {query: options = {}} = this.props;

    options = this.useQueryParams ? options : {};

    /**
     * Entity Configuration - initialize all the config toggles and values here
     *
     */
    const EntityConfig = getEntityConfigObj(entity, theme);
    let {EntityLayout} = EntityConfig;
    const {ConfigType, PillsConfig, hidePanelHeader} = EntityConfig;

    /**
     * EntityLayout is determined by passing the type from the config file
     * to the LayoutTypes object initialized at the top of this file
     *
     */
    EntityLayout = LayoutTypes[EntityConfig.EntityLayout];

    /**
     * Initialize empty container and toggle values
     *
     */
    let eventsPanel = null;
    let pillBar = null;

    const searchParams = getEntitySearchAPIParams(entityType, entityId);
    const isGeo = isGeography(entityType);
    const {globalRegistry} = this.context;
    const isArtist = ConfigType === 'Artist';

    /**
     * CityPage related variable
     */
    let isCityHeader = false;
    let isCityPage = false;
    const cityPageAttributes = this.getCityPageAttributes(entity.dynamicAttributes, entity.images);
    const {isCityPageValidAttribute = '', cityImageAttribute = ''} = cityPageAttributes;

    const {ancestors} = entity;
    const geoLevelCheckStatus =
      !this.isGeoLevelCheckDisabled && isGeo && ancestors && ancestors.geographies && ancestors.geographies.length >= 2;
    isCityPage = this.isCityPageEnabled && isGeo && (isCityPageValidAttribute || geoLevelCheckStatus);
    isCityHeader = Boolean(isCityPage && cityImageAttribute);

    let {displayType} = this.state;
    const {sortProps} = this.state;

    const defaultWebTLD = globalRegistry.getDefaultWebTLD();
    let entitySEOLinks = null;
    const isMobile = deviceType === 'phone';

    /**
     * Geography Entity Page
     *
     */
    if (isGeo) {
      const {externalSourceId, ancestors = {}} = entity;
      eventsPanel =
        isCityPage && entity && +entityId === entity.id ? (
          <ConnectedCityPage
            lat={entity.latitude}
            lng={entity.longitude}
            className="EntityPage__SeoChildLinks"
            entityId={entityId}
            entityName={entity.name}
            entityWebURI={entity.webURI}
            externalSourceId={externalSourceId}
            parentGeos={ancestors.geographies || []}
            enableSubGenreModuleMweb={isMobile}
            enableSubGenreModuleDesktop={!isMobile}
          />
        ) : (
          <>
            <EntitiesSeoLinks
              className="EntityPage__SeoChildLinks"
              parentEntityType={entityType}
              entityId={entityId}
              namespace={NAMESPACE}
              entityType={ENTITY_TYPE_GEO}
              disableNoLinksFoundMessage
            />
            <EntitiesSeoLinks
              className="EntityPage__SeoChildLinks"
              parentEntityType={entityType}
              entityId={entityId}
              namespace={NAMESPACE}
              entityType={ENTITY_TYPE_VENUE}
              hideSEOLinks={!(geographiesSEOLinkCount === 0)}
            />
          </>
        );
    }

    const jumbotronBanner = (
      <JumbotronBanner
        headerData={this.getJumbotronBannerData(this.props, isCityHeader)}
        theme={theme}
        entity={entity}
        deviceType={deviceType}
      />
    );
    const isGroupCat = isGroupingEntity(entityType) || isCategoryEntity(entityType);

    /**
     *  If eventsPanel is not generated by the partner code, we need to generated
     *  a event list panel that supports geo or all locations.  For example, grouping
     *  pages may support a partner header but not the partner event list with calendar
     * @TODO: Refactor to get the component for the eventPanel from the helper
     *
     */

    const mainEventTypeParams = 'Main|Festival|Season|Tailgate|Hospitality';
    let eventTypeParams =
      isGroupingEntity(entityType) || isCategoryEntity(entityType)
        ? {}
        : {
            eventType:
              displayType === DISPLAY_TYPE_VIP
                ? 'VIP'
                : displayType === DISPLAY_TYPE_OTHER
                ? 'Tailgate|Hospitality'
                : mainEventTypeParams,
          };

    let panelParams = {...searchParams, ...sortProps, ...eventTypeParams};

    if (!eventsPanel) {
      if (this.useQueryParams) {
        if (isGroupCat) {
          if (this.hideTabsConfigOnCategoryGrouping) {
            // Fixed display type, if there is no tab switch
            displayType = 'UPCOMING_EVENTS';
          } else {
            displayType = options.pbN === 'c' ? 'CATEGORIES' : 'UPCOMING_EVENTS';
          }
        } else if (theme.isPartner) {
          displayType =
            options.pbE === 'o'
              ? DISPLAY_TYPE_OTHER
              : options.pbC === 'c'
              ? DISPLAY_TYPE_CALENDAR
              : DISPLAY_TYPE_EVENTS_ONLY;
        } else {
          displayType =
            options.pbE === 'v'
              ? DISPLAY_TYPE_VIP
              : options.pbE === 'o'
              ? DISPLAY_TYPE_OTHER
              : DISPLAY_TYPE_EVENTS_ONLY;
        }
      }

      /**
       * Event List Panel Section
       * If geo is set, show two panels else show all geo in one panel
       *
       */
      eventTypeParams = isGroupCat
        ? {}
        : {
            eventType:
              displayType === DISPLAY_TYPE_VIP
                ? 'VIP'
                : displayType === DISPLAY_TYPE_OTHER
                ? 'Tailgate|Hospitality'
                : mainEventTypeParams,
          };

      panelParams = {...searchParams, ...sortProps, ...eventTypeParams};

      const eventPanelContainerProps = {
        id: entityId,
        searchParams: panelParams,
        sort: sortProps && sortProps.sort,
        displayType,
        title: this.formatTitle,
        enableSEOMeta: true,
        hidePanelHeader,
        entityConfigType: ConfigType,
        entityType,
      };

      if (geo && geo.key) {
        eventsPanel = (
          <>
            <EventListPanelContainer
              {...eventPanelContainerProps}
              key={`${NAMESPACE}-eventListPanel-geoIncludeRadius`}
              namespace={`${NAMESPACE}.events.geoIncludeRadius`}
            />
            <EventListPanelContainer
              {...eventPanelContainerProps}
              key={`${NAMESPACE}-eventListPanel-geoExcludeRadius`}
              namespace={`${NAMESPACE}.events.geoExcludeRadius`}
              includeRadius={false}
              globalIndexOffset={localEventIndex}
            />
            {showTrustMessages && <GuaranteeCard key={`${NAMESPACE}-guarantee`} className="guarantee__card card" />}
          </>
        );
      } else {
        eventsPanel = (
          <>
            <EventListPanelContainer
              {...eventPanelContainerProps}
              key={`${NAMESPACE}.eventListPanel-all`}
              namespace={`${NAMESPACE}.events.all`}
            />
            {showTrustMessages && <GuaranteeCard className="guarantee__card card" />}
          </>
        );
      }
    }

    /**
     * Show the Tabs for Grouping/Category Page.
     *
     */
    let categoriesPanel;
    if (isGroupCat) {
      categoriesPanel = (
        <EntitiesSeoLinks
          className="EntityPage__SeoChildLinks"
          parentEntityType={entityType}
          entityId={entityId}
          namespace={NAMESPACE}
        />
      );
      if (displayType === 'CATEGORIES') {
        eventsPanel = categoriesPanel;
      } else {
        entitySEOLinks = this.isEPLGrouping ? null : (
          <EntitiesSeoLinks
            className="EntityPage__SeoChildLinks"
            parentEntityType={entityType}
            entityId={entityId}
            namespace={NAMESPACE}
            disableNoLinksFoundMessage
          />
        );
      }
    }
    const themeData = this.getJumbotronBannerData(this.props, isCityHeader);
    const filterActions = {
      onTabChange: this.changeDisplayType,
      setSort: this.setSort,
      setSearch: this.setSearch,
    };

    // Only render the pillbar for citypage when feature toggle is on
    if ((isGeo && !isCityPage) || (isGeo && isCityPage && !this.isDateFilterEnabledOnCityPage)) {
      pillBar = null;
    } else {
      const pillBarProps = {
        config: PillsConfig,
        options,
        filterActions,
        themeData,
        noDateFilter: false,
        isArtist,
        entityType,
      };

      pillBar = <EntityPillBar {...pillBarProps} />;
    }

    /**
     * SEO Bottom Content URL
     *
     */
    const {seoBottomContentUrl, seoTitle} = getEntitySEOObj(entity);
    const entityImageClass = 'EntityImage';
    const image = getHeroImage(entityType, entity, {
      noCompress: true,
    });
    const {cloudinaryPublicId, url, source} = image;
    const src = url;
    const publicIdSrc = cloudinaryPublicId;

    const artistInfo = entity.isConcert || entity.isTheatre;
    const sideSection = (
      <div className="EntityPage__SideContainer">
        <SEOSideContent
          entityImageClass={entityImageClass}
          src={src}
          publicIdSrc={publicIdSrc}
          source={source}
          entityName={entity.name}
          entityId={entityId}
          readMoreRef={this.scrollToBottom}
          globalRegistry={globalRegistry}
          entityType={entityType}
          showSeeMore={hasSeoBottomContent}
        />
        {!isMobile && !!spotifyArtistId && (
          <SpotifyWidget artistId={spotifyArtistId} data-testid="spotify-widget" className="spotify-widget" />
        )}
        {showTrustMessages && <GuaranteeCard className="guarantee__card" />}
      </div>
    );

    let bottomImage = null;
    if (artistInfo) {
      const entityImageOptions = {
        src,
        publicIdSrc,
        ...DEFAULT_IMAGE_CONFIG,
      };
      const entityImageUrl = helper.getCloudinaryImage(entityImageOptions);
      const entityImage = entityImageUrl ? (
        <CloudinaryImage className={entityImageClass} image={entityImageUrl} alt={source} />
      ) : null;
      bottomImage = entityImage && (
        <CardImage
          className="EntityImage-container"
          image={entityImage}
          alt={source}
          attribution={source}
          useSkeleton={false}
        />
      );
    }

    const bottomSection = (
      <div className="EntityPage__BottomContainer card">
        {bottomImage}
        {seoBottomContentUrl && (
          <SeoBottomContent
            className="EntityPage__SeoBottomContent"
            seoTitle={seoTitle}
            seoBottomContentUrl={seoBottomContentUrl}
            ref={(node) => (this.scrollToBottom = node)}
            refNode={this.scrollToTop}
            expanded
          />
        )}
      </div>
    );

    /**
     * Events SEO mark up section
     * Not for GEO pages
     *
     */
    const eventSEOList = !isGeo ? <ConnectedEventSEOList entityId={entityId} searchParams={searchParams} /> : null;

    // Performer&Grouping page Venues SEO linkLists
    let venueSEOList = null;
    if (
      (this.isSEOPerformerVenuesEnable && isPerformerEntity(entityType)) ||
      (this.isSEOGroupingVenuesEnable && isGroupingEntity(entityType))
    ) {
      let venueSEOTitleType = SEOVENUESTYPE.DEFAULT;
      const {isSport, isConcert, isTheatre} = entity;
      if (isGroupingEntity(entityType)) {
        venueSEOTitleType = SEOVENUESTYPE.GROUPING;
      } else if (isSport) {
        venueSEOTitleType = SEOVENUESTYPE.SPORTS;
      } else if (isConcert) {
        venueSEOTitleType = SEOVENUESTYPE.CONCERT;
      } else if (isTheatre) {
        venueSEOTitleType = SEOVENUESTYPE.THEATER;
      }

      const venuesParams = {
        ...searchParams,
        geo,
      };
      venueSEOList = (
        <ConnectedSeoPerformerVenues
          entityType={entityType}
          entityName={entity.name}
          entityId={entityId}
          venuesParams={venuesParams}
          geo={geo}
          type={venueSEOTitleType}
          // Title = {venuesSEOTitle}
        />
      );
    }

    let noEventsPanel = noEvent ? <NoResultsPanel /> : null;

    noEventsPanel =
      isCBTModuleEnabled && !isCBTModuleException ? (
        <CBTModule
          entityName={entity.name}
          entityType={entityType}
          src={src}
          publicIdSrc={publicIdSrc}
          webUri={entity.webURI}
        />
      ) : (
        noEventsPanel
      );

    // Nearby venues section for venue pages only.
    let nearbyVenues = null;
    if (isVenueEntity(entityType) && this.isNearbyVenuesEnabled) {
      const {id, name, longitude, latitude} = entity;
      nearbyVenues = (
        <SeoNearbyVenues entityName={name} entityId={id} entityType={entityType} lat={latitude} lng={longitude} />
      );
    }

    // Venue Performers section for venue pages only.
    let venuePerformers = null;
    if (isVenueEntity(entityType) && this.isVenuePerformersEnabled) {
      const {id, name} = entity;
      venuePerformers = <ConnectedSEOVenuePerformers entityId={id} entityName={name} entityType={entityType} />;
    }

    // EventsContainer: USA design, eventsPanel: intl design
    const EntityContents = (
      <EntityLayout
        pillBar={pillBar}
        eventsPanel={eventsPanel}
        eventSEOList={eventSEOList}
        noEventsPanel={noEventsPanel}
        sideSection={sideSection}
        entitySEOLinks={entitySEOLinks}
        nearbyVenues={nearbyVenues}
        venuePerformers={venuePerformers}
        venueSEOList={venueSEOList}
        spotifyArtistId={spotifyArtistId}
      />
    );

    /**
     * Related Artists
     */
    const {categories = [], groupings = [], isSport, isConcert, id} = entity;
    const relatedArtistsPanel =
      this.isRelatedArtistsEnabled && !this.isEPLGrouping ? (
        <ConnectedRelatedArtists
          enableCarouselOnMweb
          cardNumberOnMweb={2.5}
          categories={categories}
          groupings={groupings}
          isSport={isSport && isPerformerEntity(entityType)}
          isConcert={isConcert && isPerformerEntity(entityType)}
          entityId={id}
        />
      ) : null;

    return (
      <>
        {jumbotronBanner}
        <div
          className={cs('EntityPage__container', {jumbotron: true}, {PillBar: true})}
          ref={(node) => (this.scrollToTop = node)}
        >
          {EntityContents}
          {relatedArtistsPanel}
          <div className="EntityPage__footer">
            {this.isEPLGrouping && hideSEOEntity({entityType, id}) ? null : bottomSection}
          </div>
          <FeatureToggle name="homepage.enableEmailCollection">
            <Email />
          </FeatureToggle>
        </div>
        <FeatureToggle name="common.enableInterceptModalForCA">
          <InterceptModal currentLocation={currentLocation} defaultWebTLD={defaultWebTLD} />
        </FeatureToggle>
      </>
    );
  }
}

EntityPageInner.propTypes = {
  /** Current location information */
  currentLocation: PropTypes.object,

  /** Prop indicating the device type of the client */
  deviceType: PropTypes.string,

  /** Function to dispatch actions to controller */
  dispatch: PropTypes.func,

  /** Object containing the Catalog Entity API response. */
  entity: PropTypes.object,

  /** The id associated to the Entity. */
  entityId: PropTypes.string,

  /** Object containing the URL related Params. */
  entityType: PropTypes.string,

  /** Are events loading? */
  eventsLoading: PropTypes.bool,

  /** The geo object containing the location information for the user */
  geo: PropTypes.object,

  /** The field having the count of the Geography SEO Links used by the Geo Pages to choose between Displaying Geo or Venues */
  geographiesSEOLinkCount: PropTypes.number,

  /** Object containing totals for nearby, distant and all events */
  groupedEventsTotal: PropTypes.object,

  /** The intl object */
  intl: PropTypes.object.isRequired,

  /** Controller Method to Load the Entity Info from the API. */
  loadEntity: PropTypes.func.isRequired,

  /** Object Containing React Route Params */
  localEventIndex: PropTypes.number,

  /** Object Containing the Parsed URL Information */
  location: PropTypes.object,

  /** Flag indicating if No Events were loaded in the current view. */
  noEvent: PropTypes.bool,

  /** Page ID */
  pid: PropTypes.string,

  /** The query in object format */
  query: PropTypes.object,

  /** Page Referrer */
  referrer: PropTypes.string,

  /** Query related function */
  replaceQuery: PropTypes.func.isRequired,

  /** Function to reset the redux store for the Entity page. */
  resetEntityPage: PropTypes.func.isRequired,

  /** If ssr */
  ssr: PropTypes.bool,

  /** Current Global theme object */
  theme: PropTypes.object,

  /** Cxs function */
  themeProvider: PropTypes.func,

  /** Cxs object for applying the theme. */
  updateTheme: PropTypes.func.isRequired,

  /** Whether CBT module is enabled */
  isCBTModuleEnabled: PropTypes.bool,

  /** Whether it is a CBT exception */
  isCBTModuleException: PropTypes.bool,

  /** If trust messages can be shown */
  showTrustMessages: PropTypes.bool,

  /** whether we have seo bottom content */
  hasSeoBottomContent: PropTypes.bool,

  /** Spotify Artist Id */
  spotifyArtistId: PropTypes.string,
};

export {EntityPageInner};
export default injectIntl(EntityPage);
